import { MANTRA_MODULE_IDS, MANTRA_MODULES } from './mantraModules.js';
import {
  MANTRA_GLOBAL_ROLE_RANK,
  resolveDeploymentRole,
  resolvePreferredSlotRole,
  slotRoleKey,
} from './mantraRoleHierarchy.js';
import { MANTRA_ROLES, canonicalMantraRoles } from './studioCalc.js';

export function sumRosterSpent(entries) {
  return entries.reduce((sum, entry) => sum + (Number(entry.price) || 0), 0);
}

function normalizeRoles(roles) {
  return canonicalMantraRoles(roles);
}

const PREFERRED_MATCH_BONUS = 10_000;
const SPECIALIST_BONUS = 2_000;
const SLOT_PROFILE_BONUS = 3_000;
const UNUSED_ROLE_PENALTY = { PC: 3_000, A: 2_000, T: 1_500, W: 1_500 };

function scorePlayerForSlot(playerRoles, accepted, preferredSlotRole) {
  const roles = normalizeRoles(playerRoles);
  const matching = roles.filter((r) => accepted.includes(r));
  if (matching.length === 0) return -1;

  const deployRole = resolveDeploymentRole(roles, accepted);
  let score = 100 - (MANTRA_GLOBAL_ROLE_RANK[deployRole] ?? 99);

  if (deployRole && deployRole === preferredSlotRole) {
    score += PREFERRED_MATCH_BONUS;
  }
  if (matching.length === 1) {
    score += SPECIALIST_BONUS;
  }
  if (matching.length > 1 && slotRoleKey(matching) === slotRoleKey(accepted)) {
    score += SLOT_PROFILE_BONUS;
  }
  if (accepted.length === 1 && roles.length > 1) {
    score -= 2_500;
  }
  if (roles.includes('E') && accepted.includes('W') && accepted.includes('A') && !accepted.includes('E')) {
    score -= 5_000;
  }
  for (const r of roles) {
    if (!accepted.includes(r) && UNUSED_ROLE_PENALTY[r]) {
      score -= UNUSED_ROLE_PENALTY[r];
    }
  }

  return score;
}

/** Slot con meno ruoli accettati prima (E prima di W/A) — poi indice modulo. */
function sortedSlotIndices(moduleSlots) {
  return moduleSlots.map((_, i) => i).sort((a, b) => {
    const lenDiff = moduleSlots[a].length - moduleSlots[b].length;
    if (lenDiff !== 0) return lenDiff;
    return a - b;
  });
}

function pickPlayerIndexForSlot(available, accepted) {
  const acceptedUpper = accepted.map((r) => r.toUpperCase());
  const preferredRole = resolvePreferredSlotRole(acceptedUpper);
  let bestIndex = -1;
  let bestScore = -1;
  for (let pi = 0; pi < available.length; pi++) {
    if (available[pi].used) continue;
    const score = scorePlayerForSlot(available[pi].roles, acceptedUpper, preferredRole);
    if (score > bestScore) {
      bestScore = score;
      bestIndex = pi;
    }
  }
  return bestIndex;
}

function assignPlayersToModuleSlots(moduleSlots, players) {
  const available = players.map((p) => ({
    playerKey: p.playerKey,
    name: p.name ?? null,
    roles: normalizeRoles(p.roles ?? []),
    used: false,
  }));
  const assigned = new Array(moduleSlots.length).fill(null);
  const filledSlots = new Set();

  for (const slotIndex of sortedSlotIndices(moduleSlots)) {
    const accepted = moduleSlots[slotIndex].map((r) => r.toUpperCase());
    const pi = pickPlayerIndexForSlot(available, accepted);
    if (pi >= 0) {
      available[pi].used = true;
      assigned[slotIndex] = available[pi];
      filledSlots.add(slotIndex);
    }
  }

  return { assigned, filledSlots };
}

function coverageForModule(slots, players) {
  const { filledSlots } = assignPlayersToModuleSlots(slots, players);

  const missing = [];
  for (let i = 0; i < slots.length; i++) {
    if (!filledSlots.has(i)) {
      const preferred = resolvePreferredSlotRole(slots[i]);
      missing.push(preferred || slots[i][0]);
    }
  }

  const filled = filledSlots.size;
  const total = slots.length;
  let status;
  if (filled === total) status = 'complete';
  else if (filled === 0) status = 'empty';
  else status = 'partial';

  return { filled, total, missing, status };
}

export function computeModuleCoverage(players, modules = MANTRA_MODULES) {
  const results = MANTRA_MODULE_IDS.filter((id) => modules[id]).map((id) => {
    const row = coverageForModule(modules[id], players);
    return { id, ...row };
  });

  results.sort((a, b) => {
    const completeA = a.status === 'complete' ? 1 : 0;
    const completeB = b.status === 'complete' ? 1 : 0;
    if (completeB !== completeA) return completeB - completeA;
    if (b.filled !== a.filled) return b.filled - a.filled;
    return a.id.localeCompare(b.id);
  });

  return results;
}

export function primaryMantraRole(roles) {
  const normalized = normalizeRoles(roles ?? []);
  return normalized.find((r) => MANTRA_ROLES.includes(r)) || 'Altri';
}

/** Quanti slot liberi restano per un ruolo (target − presi), mai negativo. */
export function roleMissingCount(ownedCount, target) {
  const owned = Number(ownedCount) || 0;
  const tgt = Number(target) || 0;
  return Math.max(0, tgt - owned);
}

export function computeRosaSummary({ entries, budget, roleTargets, maxPlayers, playersByKey }) {
  const spent = sumRosterSpent(entries);
  const owned = entries.length;
  const residui = budget - spent;
  const slots = maxPlayers != null
    ? Math.max(0, Number(maxPlayers) || 0)
    : Object.values(roleTargets || {}).reduce((sum, n) => sum + n, 0);
  const media = owned ? Math.round(spent / owned) : 0;

  let topAcquistoName = '—';
  let topAcquistoPrice = 0;
  if (entries.length > 0) {
    let topEntry = entries[0];
    for (const entry of entries) {
      if ((Number(entry.price) || 0) > (Number(topEntry.price) || 0)) {
        topEntry = entry;
      }
    }
    topAcquistoPrice = Number(topEntry.price) || 0;
    const player = playersByKey.get(topEntry.key);
    topAcquistoName = player?.name ?? '—';
  }

  return {
    spent,
    residui,
    owned,
    slots,
    media,
    topAcquistoName,
    topAcquistoPrice,
  };
}

export function assignModuleSlots(players, moduleId, modules = MANTRA_MODULES) {
  const moduleSlots = modules[moduleId];
  if (!moduleSlots) {
    return [];
  }

  const rosterPlayers = players.map((p) => ({
    playerKey: p.playerKey,
    name: p.name ?? null,
    roles: p.roles ?? [],
  }));

  const { assigned } = assignPlayersToModuleSlots(moduleSlots, rosterPlayers);

  return moduleSlots.map((acceptedRoles, slotIndex) => {
    const match = assigned[slotIndex];
    const preferredRole = resolvePreferredSlotRole(acceptedRoles);
    const deployedRole = match
      ? resolveDeploymentRole(match.roles, acceptedRoles)
      : null;
    return {
      slotIndex,
      acceptedRoles,
      preferredRole,
      deployedRole,
      playerKey: match ? match.playerKey : null,
      name: match ? match.name : null,
    };
  });
}
